import { useState, useCallback } from "react";
import {
  useStartTriviaGameMutation,
  useGetTriviaGameSessionQuery,
  useSubmitTriviaAnswerMutation,
  useCompleteTriviaGameMutation,
  useAbandonTriviaGameMutation,
} from "../store/api/mlbApi";

/**
 * Custom hook for managing trivia game sessions
 * Provides a clean interface for game flow management
 */
export const useTriviaGame = () => {
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [gameState, setGameState] = useState("idle"); // idle, playing, completed, abandoned
  const [currentQuestions, setCurrentQuestions] = useState(null);

  const [startGame, { isLoading: isStarting }] = useStartTriviaGameMutation();
  const [submitAnswer, { isLoading: isSubmitting }] =
    useSubmitTriviaAnswerMutation();
  const [completeGame, { isLoading: isCompleting }] =
    useCompleteTriviaGameMutation();
  const [abandonGame, { isLoading: isAbandoning }] =
    useAbandonTriviaGameMutation();

  const { data: sessionData, isLoading: isLoadingSession } =
    useGetTriviaGameSessionQuery(currentSessionId, {
      skip:
        !currentSessionId ||
        currentSessionId === null ||
        currentSessionId === undefined,
    });

  const startNewGame = useCallback(
    async (gameConfig) => {
      try {
        const result = await startGame(gameConfig).unwrap();
        if (result.success) {
          setCurrentSessionId(result.sessionId);
          setCurrentQuestions(result.questions);
          setGameState("playing");
          return {
            success: true,
            sessionId: result.sessionId,
            questions: result.questions,
          };
        }
        return { success: false, error: result.error };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to start game",
        };
      }
    },
    [startGame]
  );

  const answerQuestion = useCallback(
    async (questionId, answer, timeSpent) => {
      if (!currentSessionId)
        return { success: false, error: "No active session" };

      try {
        const result = await submitAnswer({
          sessionId: currentSessionId,
          answerData: { questionId, answer, timeSpent },
        }).unwrap();

        if (result.success) {
          return {
            success: true,
            isCorrect: result.isCorrect,
            correctAnswer: result.correctAnswer,
            sourceData: result.sourceData, // Include sourceData for stat comparison
            points: result.points,
            streak: result.streak,
            currentScore: result.currentScore,
            currentAccuracy: result.currentAccuracy,
            achievementsUnlocked: result.achievementsUnlocked,
            nextQuestion: result.nextQuestion,
          };
        }
        return { success: false, error: result.error };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to submit answer",
        };
      }
    },
    [currentSessionId, submitAnswer]
  );

  const finishGame = useCallback(async () => {
    if (!currentSessionId)
      return { success: false, error: "No active session" };

    try {
      const result = await completeGame(currentSessionId).unwrap();
      if (result.success) {
        setGameState("completed");
        return {
          success: true,
          finalScore: result.finalScore,
          accuracy: result.accuracy,
          totalTime: result.totalTime,
          correctAnswers: result.correctAnswers,
          totalQuestions: result.totalQuestions,
          experiencePoints: result.experiencePoints,
          newLevel: result.newLevel,
          achievementsUnlocked: result.achievementsUnlocked,
          shareToken: result.shareToken,
          username: result.username,
          sport: result.sport,
          gameMode: result.gameMode,
        };
      }
      return { success: false, error: result.error };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to complete game",
      };
    }
  }, [currentSessionId, completeGame]);

  const quitGame = useCallback(async () => {
    if (!currentSessionId)
      return { success: false, error: "No active session" };

    try {
      const result = await abandonGame(currentSessionId).unwrap();
      if (result.success) {
        setGameState("abandoned");
        setCurrentSessionId(null);
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to abandon game",
      };
    }
  }, [currentSessionId, abandonGame]);

  const resetGame = useCallback(() => {
    setCurrentSessionId(null);
    setCurrentQuestions(null);
    setGameState("idle");
  }, []);

  const bootstrapSession = useCallback((result) => {
    setCurrentSessionId(result.sessionId);
    setCurrentQuestions(result.questions);
    setGameState("playing");
    return { success: true, sessionId: result.sessionId, questions: result.questions };
  }, []);

  const combinedSessionData =
    currentQuestions && currentSessionId
      ? {
          ...(sessionData?.session || {}),
          questions: currentQuestions,
          score: sessionData?.session?.score ?? 0,
        }
      : sessionData?.session
        ? {
            ...sessionData.session,
            questions: currentQuestions || [],
          }
        : currentQuestions
          ? { questions: currentQuestions, score: 0 }
          : null;

  return {
    // Game state
    currentSessionId,
    gameState,
    sessionData: combinedSessionData,

    // Actions
    startNewGame,
    bootstrapSession,
    answerQuestion,
    finishGame,
    quitGame,
    resetGame,

    // Loading states
    isStarting,
    isSubmitting,
    isCompleting,
    isAbandoning,
    isLoadingSession,

    // Computed state
    isGameActive: gameState === "playing" && currentSessionId,
    canStartNewGame:
      gameState === "idle" ||
      gameState === "completed" ||
      gameState === "abandoned",
  };
};
